import { DataGridPro } from '@mui/x-data-grid-pro';
import { useSnackbar } from 'notistack';
import * as React from 'react';

import { DataAPIContext, GET_LIST } from '../api';
import FormLicenseeContext from './FormLicenseeContext';

const RecordList = (props) => {

    const [paginationModel, setPaginationModel] = React.useState({});

    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [pageInfo, setPageInfo] = React.useState({ totalRowCount: 0 });

    const rowCountRef = React.useRef(pageInfo?.totalRowCount || 0);

    const [filterList, setFilterList] = React.useState([]);

    const rowCount = React.useMemo(() => {
        if (pageInfo?.totalRowCount !== undefined) {
            rowCountRef.current = pageInfo.totalRowCount;
        }
        return rowCountRef.current;
    }, [pageInfo?.totalRowCount]);

    const [, setAsyncCountHandle] = React.useState(null);


    const dataAPI = React.useContext(DataAPIContext);
    const formLicensee = React.useContext(FormLicenseeContext);
    const resource = props.resource;

    const { enqueueSnackbar } = useSnackbar();

    const defaultSortModel = props.sortModel || [{ field: 'id', sort: 'asc' }];
    const [sortModel] = React.useState(defaultSortModel);

    const handlePagination = (params, events, details) => {
        setPaginationModel(params);
    };

    const _sortModel2sort = (sm) => {
        if (sm && sm.length > 0) {
            return { field: sm[0].field, order: sm[0].sort };
        } else if (defaultSortModel && defaultSortModel.length > 0) {
            return { field: defaultSortModel[0].field, order: defaultSortModel[0].sort };
        }
    };

    const convertFilters = (flist) => {
        return (flist || [])
            .map((f) => {
                try {
                    return {
                        field: f.columnField || f.field,
                        op: f.operatorValue || f.operator,
                        value: f.value,
                    };
                } catch (e) {
                    console.warning('RecordList.convertFilters: filter=', f, ', exception=', e, ', columns=', props.columns);
                    return null;
                }
            })
            .filter((e) => !!e);
    };

    const onFilterChange = React.useCallback((filterModel) => {
        if (filterModel && filterModel.items) {
            const fl = filterModel.items;
            setFilterList(fl);
        } else {
            setFilterList([]);
        }
    });

    const columns = props.columns;

    React.useEffect(() => { 
        if (props.isFilterChanged) {
            setPaginationModel((prev) => ({
                ...prev,
                page: 0,
            }));
        }
    }, [props.isFilterChanged]);

    React.useEffect(() => {
        if (props.staticOptions) {
            setPageInfo({ totalRowCount: props.staticOptions.length });
            return;
        }
        let active = true;
        let ac = new AbortController();

        (async () => {
            const page = paginationModel.page + 1;
            const perPage = paginationModel.pageSize;
            const filter = {
                query: props.query,
                filters: JSON.stringify([...convertFilters(filterList), ...(props.apiFilters || [])]),
            };
            const effectiveLicensee = formLicensee || props.franchiseTo;
            if (effectiveLicensee) {
                filter.franchise_to = effectiveLicensee;
            }
            const params = {
                signal: ac.signal,
                convert: props.convert,
                pagination: { page, perPage },
                sort: _sortModel2sort(sortModel),
                filter,
                urlSuffix: props.urlSuffix,
            };
            try {
                const res = await dataAPI(GET_LIST, resource, params);
                if (!active) {
                    return;
                }
                if (res.statusCode === 500) {
                    enqueueSnackbar('Sorry, our server is under maintenance, for more information please contact support', { variant: 'error' });
                }
                console.log('jt debug total: ', res.total);
                if (res.total < 0) {
                    console.log('recordList: data fetched', res);
                    const num_to_show_on_page = (res.data || []).length;
                    const prev_pages_total = (page - 1) * perPage;
                    if((res.data || []).length >= perPage){
                        setPageInfo({ totalRowCount: prev_pages_total + num_to_show_on_page + 1 }); // set to something bigger than the current page size to show the paginator controls
                    }
                    else {
                        setPageInfo({ totalRowCount: prev_pages_total + num_to_show_on_page });
                    }
                    if (res.async_count_handle) {
                        setAsyncCountHandle(res.async_count_handle);
                    }
                } else {
                    setPageInfo({ totalRowCount: res.total });
                }
                setLoading(false);
                setRows(res.data);
                props.onRowsChanged && props.onRowsChanged(res.data);
            } catch (e) {
                if (e.name !== 'AbortError') {
                    throw e;
                }
            }
        })();

        return () => {
            active = false;
            ac.abort();
        };
    }, [paginationModel, props.query, props.apiFilters, props.franchiseTo, formLicensee]);

    React.useEffect(() => {
        setPaginationModel({
            page: 0,
            pageSize: props.perPage,
        });
    }, []);

    return (
        <DataGridPro
            {...props}

            pagination
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={handlePagination}
            loading={loading}

            checkboxSelection
            keepNonExistentRowsSelected

            rows={rows}
            rowCount={rowCount}
            columns={columns}

            filterMode="server"
            onFilterModelChange={onFilterChange}

            density="compact"
            headerHeight={props.headerHeight != null ? props.headerHeight : 56}
            sx={{
                '&.MuiDataGrid-root': {
                    border: props.borderDisplay,
                },
                '& .MuiDataGrid-columnHeaders': {
                    display: props.headerDisplay,
                },
                '& .MuiDataGrid-cell': {
                    border: props.borderDisplay,
                },
                '& .MuiDataGrid-row:last-child': {
                },
                '& .MuiDataGrid-virtualScrollerContent': {
                    paddingBottom: props.isExporting ? `${400 + paginationModel.pageSize*4}px` : null,
                    marginTop: props.isExporting ? 5 : 0,
                },
                '& .MuiDataGrid-footerContainer': {
                    position: props.isExporting ? 'absolute' : null,
                    top: props.isExporting ? 0 : null,
                    fontStyle: props.isExporting ? "italic" : null
                },
                '& .MuiTablePagination-displayedRows:before': {
                    content: props.isExporting ? '"Message history records "' : null,
                },
                '& .MuiDataGrid-toolbarContainer': {
                    display: props.isExporting ? "none" : null
                },
                '& .MuiTablePagination-actions': {
                    display: props.isExporting ? "none" : null
                },
                '& .MuiTablePagination-input': {
                    display: props.isExporting ? "none" : null
                },
                '& .MuiTablePagination-selectLabel': {
                    display: props.isExporting ? "none" : null
                },
            }}
        />
    );
}

export default RecordList;
